import React from 'react';
import { Alert } from '../ui';
import { useHostCapabilities } from '../../hooks/useHostCapabilities';
import { isCarSettingKey, type HostCapabilityKey } from '../../utils/hostCapabilities';

/**
 * Spec 014a FR-028: shown where a switched-off section or button would be,
 * e.g. "Lenvoy manages trip fees on this site."
 */
export function ManagedByBrandNote({ capability }: { capability: HostCapabilityKey }) {
  const { noteFor } = useHostCapabilities();
  return (
    <Alert tone="info" data-testid={`managed-by-brand-${capability}`}>
      {noteFor(capability)}
    </Alert>
  );
}

/** What an admin's edit does in a section hosts here do not see. */
function adminOnlyExtra(capability: HostCapabilityKey): string {
  // Spec 014c: delivery has no brand value; the car's airports are kept for later.
  if (capability === 'carDelivery') {
    return ' Changes here are saved but guests on this site do not see them.';
  }
  return isCarSettingKey(capability)
    ? " Changes here update this car's own value, which trips on this site do not use."
    : '';
}

/**
 * Spec 014b US-1: above a section the brand took from its hosts, for its
 * admin. A car setting the admin edits here changes only the car's own value,
 * which trips on this site do not use.
 */
export function AdminOnlyNote({ capability }: { capability: HostCapabilityKey }) {
  const { noteFor } = useHostCapabilities();
  const extra = adminOnlyExtra(capability);
  return (
    <Alert tone="warning" title="Admin only" data-testid={`admin-only-note-${capability}`}>
      {`Hosts on this site do not see this. ${noteFor(capability)}${extra}`}
    </Alert>
  );
}

export default ManagedByBrandNote;
